import model from './annotation-layer.tools/testInitialModel';
import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  EventEmitter,
  Output,
  enableProdMode,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild
} from '@angular/core';
import * as d3 from 'd3';
import { SimplifyAP as simplify } from 'simplify-ts';
import { BehaviorSubject, Subject } from 'rxjs';
import { ModelItemType, AnnotationModelItem, RectangleData, CircleData } from './interfaces';
import * as _ from 'lodash';
import { ActionType } from './annotation-tools.actions';
import {
  ResizeHandles,
  drawCircle,
  drawEllipse,
  drawFreehand,
  drawNumbering,
  drawRectangle,
  drawSvg,
  generateUID,
  resizeCircle,
  resizeEllipse,
  resizeDragableOnly,
  resizeRectangle,
  resizeSvg
} from './annotation-layer.tools';
import { max } from 'moment';
export interface AnnotationAction {
  name: string;
  data: any;
}

@Component({
    selector: 'app-annotation-layer',
    templateUrl: './annotation-layer.component.html',
    styleUrls: ['./annotation.component.scss']
    // styleUrls: ['./annotation-layer.component.scss']
    ,
    standalone: false
})
export class AnnotationLayerComponent implements OnInit, AfterViewInit {
  empty = {
    size: [],
    annotations: {
      items: []
    },
    backgroundImage: null,
    backgroundImageUrl: null
  };
  @Input() undoTrigger: Subject<any>;
  @Input() saveTrigger: Subject<any>;
  @Input() model = this.empty;
  @Output() getOutput = new EventEmitter();

  /**
   * svg
   */
  selectedSvgElement;

  /**
   * stroke
   */
  _strokeWidth = 5;

  get strokeWidth() {
    return this._strokeWidth;
  }
  set strokeWidth(value) {
    this._strokeWidth = value;
    if (value && this.selectionItem) {
      this.selectionItem.data.strokeWidth = value;
      this.redrawSingle(this.selection);
    }
  }

  /**
   * color
   */

  _selectedColor = 'rgb(0, 0, 255)';

  get selectedColor() {
    return this._selectedColor;
  }
  set selectedColor(value) {
    this._selectedColor = value;
    if (value && this.selectionItem) {
      this.selectionItem.data.color = value;
      this.redrawSingle(this.selection);
    }
  }

  /**
   * actions
   */
  _selectedAction: ActionType | null = null;

  get selectedAction() {
    return this._selectedAction;
  }
  set selectedAction(value: ActionType | null) {
    this._selectedAction = value;
    if (value) {
      this.select(null);
    }
    if (value === null) {
      this.svg.style('cursor', 'default');
    } else if (
      value === ActionType.rectangle ||
      value === ActionType.circle ||
      value === ActionType.freeHand ||
      value === ActionType.numbering ||
      value === ActionType.svgElement ||
      value === ActionType.ellipse
    ) {
      this.svg.style('cursor', 'crosshair');
    } else if (value === ActionType.zoomIn) {
      this.svg.style('cursor', 'zoom-in');
    } else if (value === ActionType.zoomOut) {
      this.svg.style('cursor', 'zoom-out');
    }
    this.redraw();
  }

  get numberingIndex(): number {
    return Math.max(
      0,
      ...this.model.annotations.items.filter((item) => item.type === 'number').map((item) => item.data.number)
    );
  }

  model$ = new BehaviorSubject<any>(this.empty);

  private _action: AnnotationAction;

  public get action(): AnnotationAction {
    return this._action;
  }
  @Input() public set action(v: AnnotationAction) {
    this._action = v;
    this.action$.next(v);
  }

  private action$ = new BehaviorSubject<AnnotationAction>(null);

  @Output() annotations = new EventEmitter();

  outputObject = {
    size: [0, 0],
    annotations: {
      items: []
    },
    svg: null
  };

  root: any;
  selectionLayer: any;
  resizeHandles: any;
  @ViewChild('svgtarget', { static: true }) svgTarget: ElementRef;
  svg: any;
  image: any;
  imgWidth = 0;
  imgHeight = 0;

  selection = null;
  get selectionItem() {
    return this.model.annotations.items.find((i) => i.id === this.selection);
  }

  drawing = false;

  /**
   * zoom
   */
  zoomD3: any;
  zoomStep = 0;
  maxZoomStep = 9;
  minZoomStep = -9;

  /**
   * modal
   */
  showModal = false;

  get description() {
    return this.selectionItem?.data?.description;
  }
  set description(value) {
    this.selectionItem.data.description = value;
    this.redraw();
  }

  private itemMap = {
    circle: (wrapper, item: AnnotationModelItem) => drawCircle(wrapper, item),
    ellipse: (wrapper, item: AnnotationModelItem) => drawEllipse(wrapper, item),
    number: (wrapper, item: AnnotationModelItem) => drawNumbering(wrapper, item),
    rectangle: (wrapper, item: AnnotationModelItem) => drawRectangle(wrapper, item),
    freehand: (wrapper, item: AnnotationModelItem) => drawFreehand(wrapper, item),
    svgElement: (wrapper, item: AnnotationModelItem) => drawSvg(wrapper, item)
  };

  private resizeMap = {
    circle: (item: AnnotationModelItem, handler: string, edx, edy) =>
      handler == 'body' ? resizeDragableOnly(item, handler, edx, edy) : resizeCircle(item, handler, edx, edy),
    ellipse: (item: AnnotationModelItem, handler: string, edx, edy) =>
      handler == 'body' ? resizeDragableOnly(item, handler, edx, edy) : resizeEllipse(item, handler, edx, edy),
    rectangle: (item: AnnotationModelItem, handler: string, edx, edy) =>
      handler == 'body' ? resizeDragableOnly(item, handler, edx, edy) : resizeRectangle(item, handler, edx, edy),
    svgElement: (item: AnnotationModelItem, handler: string, edx, edy) =>
      handler == 'body' ? resizeDragableOnly(item, handler, edx, edy) : resizeSvg(item, handler, edx, edy),
    number: (item: AnnotationModelItem, handler: string, edx, edy) => resizeDragableOnly(item, handler, edx, edy),
    freehand: (item: AnnotationModelItem, handler: string, edx, edy) => {
      resizeDragableOnly(item, handler, edx, edy);
    },
    default: (item: AnnotationModelItem, handler: string, edx, edy) => {
      resizeDragableOnly(item, handler, edx, edy);
    }
  };

  onListItemClick(item: string) {
    switch (item) {
      case 'deleteShape':
        this.removeFromOutputObject(this.selection);
        break;
      case 'description':
        this.showModal = true;
        break;
      case 'zoomIn':
        this.zoom('in');
        break;
      case 'zoomOut':
        this.zoom('out');
        break;
      default:
        break;
    }
  }

  zoom(direction: 'in' | 'out') {
    direction === 'in' ? (this.zoomStep += 1) : (this.zoomStep -= 1);
    if (this.zoomStep > this.maxZoomStep) {
      this.zoomStep = this.maxZoomStep;
    }
    if (this.zoomStep < this.minZoomStep) {
      this.zoomStep = this.minZoomStep;
    }
    this.svg.style('transform', `scale(${100 + this.zoomStep * 10}%)`);
  }

  constructor() {}

  ngOnInit() {
    //const svgDimmensions = this.svg.node().getBBox();
    //this.outputObject.size = [svgDimmensions.width, svgDimmensions.height];
    this.saveTrigger.subscribe(() => this.save());
  }

  calcImageSize(imageWidth, imageHeight, rectWidth, rectHeight) {
    // Calculer les rapports de hauteur et de largeur
    const heightRatio = imageHeight / rectHeight;
    const widthRatio = imageWidth / rectWidth;

    if (heightRatio < 1 || widthRatio < 1) {
      return {
        height: imageHeight,
        width: imageWidth
      };
    }

    // Déterminer si l'image est plus haute ou plus large que le rectangle
    if (heightRatio > widthRatio) {
      // L'image est plus haute que large, redimensionner en fonction de la hauteur
      return {
        height: imageHeight / heightRatio,
        width: imageWidth / heightRatio
      };
    } else {
      // L'image est plus large que haute, redimensionner en fonction de la largeur
      return {
        height: imageHeight / widthRatio,
        width: imageWidth / widthRatio
      };
    }
  }

  ngAfterViewInit() {
    const target = d3.select(this.svgTarget.nativeElement);
    this.svg = target.append('svg');
    this.image = this.svg
      .append('svg:image')
      .attr('image-rendering', 'optimizeQuality')
      .attr('xlink:href', this.model.backgroundImage || this.model.backgroundImageUrl)
      .attr('x', 0)
      .attr('y', 0);
    this.root = this.svg.append('g').attr('id', 'root');
    this.selectionLayer = this.svg.append('g').attr('id', 'selection');
    this.resizeHandles = new ResizeHandles(this.svg, this.selectionLayer, this);

    if (this.model.backgroundImage || this.model.backgroundImageUrl) {
      const i = new Image();
      i.onload = () => {
        const targetRect = this.svgTarget.nativeElement.getBoundingClientRect();
        const isize = this.calcImageSize(
          i.width,
          i.height,
          Math.min(targetRect.width, 1024),
          Math.min(targetRect.height, 1024)
        );
        this.imgWidth = isize.width;
        this.imgHeight = isize.height;
        this.svg
          .attr('preserveAspectRatio', 'xMinYMin meet')
          .attr('viewBox', `0 0 ${this.imgWidth} ${this.imgHeight}`)
          .attr('width', `${this.imgWidth}` + 'px')
          .attr('height', `${this.imgHeight}` + 'px');
        this.image.attr('width', this.imgWidth + 'px').attr('height', this.imgHeight + 'px');
      };

      i.src = this.model.backgroundImage || this.model.backgroundImageUrl;
      this.redraw();
    }

    const resizeControls = this.resizeHandles.getResizeControls();
    for (const key in resizeControls) {
      resizeControls[key].call(
        d3
          .drag()
          .on('start', this.dragstarted.bind(this))
          .on('drag', this.draggingHandles.bind(this, key))
          .on('end', this.dragended.bind(this))
      );
    }

    this.redraw();
    this.outputObject.svg = this.svg;
    this.outputObject.annotations = _.cloneDeep(this.model.annotations);

    /*this.annotations.emit(this.outputObject);
    this.model$.next(this.model);
    this.model$.subscribe((model) => {
      this.model = _.cloneDeep(model);
      this.redraw();
    });*/

    const line = d3
      .line()
      .curve(d3.curveCardinal)
      .x((d) => d[0])
      .y((d) => d[1]);

    let ptData = [];

    const self = this;
    const onStart = (e: any) => {
      if (e instanceof MouseEvent && e.button != 0) {
        this.selectedAction = null;
        this.select(null);
      }
      let coordinates;
      if (e instanceof MouseEvent) {
        coordinates = d3.pointer(event);
      } else {
        coordinates = d3.pointers(event)[0];
      }
      if (
        self.selectedAction === ActionType.rectangle ||
        self.selectedAction === ActionType.circle ||
        self.selectedAction === ActionType.freeHand ||
        self.selectedAction === ActionType.numbering ||
        self.selectedAction === ActionType.svgElement ||
        self.selectedAction === ActionType.ellipse
      ) {
        const newId = generateUID();
        if (this.selectedAction === ActionType.rectangle) {
          this.model.annotations.items.push({
            id: newId,
            type: 'rectangle',
            data: {
              position: coordinates,
              color: this._selectedColor,
              strokeWidth: this._strokeWidth,
              size: [50, 50]
            }
          });
        }
        if (this.selectedAction === ActionType.circle) {
          this.model.annotations.items.push({
            id: newId,
            type: 'circle',
            data: {
              position: coordinates,
              color: this._selectedColor,
              strokeWidth: this._strokeWidth,
              radius: 30
            }
          });
        }
        if (this.selectedAction === ActionType.numbering) {
          this.model.annotations.items.push({
            id: newId,
            type: 'number',
            data: {
              position: coordinates,
              color: this._selectedColor,
              strokeWidth: this._strokeWidth,
              description: '',
              number: this.numberingIndex + 1
            }
          });
          this.selectedAction = null;
          this.showModal = true;
        }
        if (this.selectedAction === ActionType.freeHand) {
          this.model.annotations.items.push({
            id: newId,
            type: 'freehand',
            data: {
              position: coordinates,
              color: this._selectedColor,
              strokeWidth: this._strokeWidth,
              path: ''
            }
          });
          this.drawing = true;
        }
        if (this.selectedAction === ActionType.svgElement) {
          this.model.annotations.items.push({
            id: newId,
            type: 'svgElement',
            data: {
              position: coordinates,
              color: this._selectedColor,
              strokeWidth: this._strokeWidth,
              element: this.selectedSvgElement,
              size: [50, 50]
            }
          });
        }
        if (this.selectedAction === ActionType.ellipse) {
          this.model.annotations.items.push({
            id: newId,
            type: 'ellipse',
            data: {
              position: coordinates,
              color: this._selectedColor,
              strokeWidth: this._strokeWidth,
              size: [50, 50]
            }
          });
        }
        this.redraw();
        this.select(newId);
      } else if (self.selectedAction === ActionType.zoomIn || self.selectedAction === ActionType.zoomOut) {
        if (this.selectedAction === ActionType.zoomIn) {
          this.zoom('in');
        } else if (this.selectedAction === ActionType.zoomOut) {
          this.zoom('out');
        }
      } else if (this.selection) {
        this.select(null);
      }
    };

    const onStop = (e) => {
      if (this.selectedAction != null) {
        e.stopPropagation();
        if (this.drawing && this.selectedAction === 'freeHand') {
          this.drawing = false;
          this.selectionItem.data.path = line(simplify(ptData));
          ptData = [];
        }
        if (self.selectedAction === ActionType.zoomIn || self.selectedAction === ActionType.zoomOut) {
        } else {
          this.selectedAction = null;
        }

        this.redraw();
      }
    };

    const onMove = (e: any) => {
      e.stopPropagation();
      let point: any;
      if (e instanceof MouseEvent) {
        point = d3.pointer(event);
      } else {
        point = d3.pointers(event)[0];
      }

      if (this.drawing && this.selectedAction === 'freeHand' && this.selectionItem) {
        const pointinsidegroup = [
          point[0] - this.selectionItem.data.position[0],
          point[1] - this.selectionItem.data.position[1]
        ];
        ptData.push(pointinsidegroup);
        this.selectionItem.data.path = line(ptData);
        this.redrawSingle(this.selection);
      }
    };

    this.svg
      .on('mousedown', onStart)
      .on('touchstart', onStart)
      .on('touchend', onStop)
      .on('touchleave', onStop)
      .on('mouseup', onStop)
      .on('mouseleave', onStop)
      .on('mousemove', onMove)
      .on('touchmove', onMove)
      .on('contextmenu', (d, i) => {
        this.selectedAction = null;
      });
  }

  addDrag(wrapper, item, element) {
    const w = d3.select(wrapper);
    const self = this;
    if (this.selection === item.id && !this.drawing) {
      this.resizeHandles.drawResizeHandles(wrapper, item, this.svg, {
        zoomStep: self.zoomStep
      });
    } else {
      w.on('mousedown.drag', null);
    }
  }

  draggingHandles(handler, event: any, d) {
    event.sourceEvent.stopPropagation();
    const coordinates = d3.pointer(event);
    let item = this.selectionItem;

    if (event.identifier == 'mouse') {
      if (this.resizeMap[item.type]) {
        this.resizeMap[item.type](item, handler, event.sourceEvent.movementX, event.sourceEvent.movementY);
      } else {
        this.resizeMap['default'](item, handler, event.sourceEvent.movementX, event.sourceEvent.movementY);
      }
      this.redrawSingle(item.id, { preventCircular: true });
    } else if (event.dx !== 0 || event.dy !== 0) {
      if (this.resizeMap[item.type]) {
        this.resizeMap[item.type](item, handler, event.dx, event.dy);
      } else {
        this.resizeMap['default'](item, handler, event.dx, event.dy);
      }
      this.redrawSingle(item.id, { preventCircular: true });
    }
  }

  dragended(event: any, d) {
    event.sourceEvent.stopPropagation();
  }

  lastClickDate = Date.now();
  delay = 200;
  dragstarted(event) {
    event.sourceEvent.stopPropagation();
    let item = this.selectionItem;
    let now = Date.now();
    if (now - this.lastClickDate < this.delay) {
      this.edit(item?.id);
    }
    this.lastClickDate = now;
  }

  addSelectable(wrapper, item, element) {
    const w = d3.select(wrapper);
    w.on('click', null);
    w.on('touchend', null);
    w.on('dblclick', null);
    if (this.selection !== item.id && !this.drawing) {
      element.style('cursor', 'pointer');
      w.on('click', (e) => {
        e.stopPropagation();
        this.select(item?.id);
      });
      w.on('touchend', (e) => {
        e.stopPropagation();
        this.select(item?.id);
      });
      w.on('dblclick', (e) => {
        e.stopPropagation();
        this.select(item?.id);
        this.edit(item?.id);
      });
    } else {
      element.style('cursor', 'default');
    }
  }

  public redraw(options: any = {}) {
    const { preventAfterEffects = false } = options;
    this.root.selectAll('g').remove();
    const items = this.model.annotations.items;
    const self = this;
    this.resizeHandles.clearResizeHandles();
    this.root
      .selectAll('g')
      .data(items)
      .join('g')
      .attr('id', (item) => item.id)
      .attr('transform', (item) => `translate(${item.data.position[0]},${item.data.position[1]})`)
      .each(function (d, e) {
        const element = self.itemMap[d.type](this, d);
        if (!self.selectedAction && !preventAfterEffects) {
          self.addSelectable(this, d, element);
          self.addDrag(this, d, element);
        }
      });
  }

  public redrawSingle(id, options: any = {}) {
    const { preventCircular = false } = options;
    try {
      const item = this.model.annotations.items.find((i) => i.id === id);
      const self = this;
      if (id !== this.selection) {
        this.resizeHandles.clearResizeHandles();
      }

      const g = this.root.select(`g[id='${id}']`).node()
        ? this.root.select(`g[id='${id}']`)
        : this.root.append('g').attr('id', item.id);

      g.data([item])
        .attr('transform', (item) => `translate(${item.data.position[0]},${item.data.position[1]})`)
        .each(function (d, e) {
          const element = self.itemMap[d.type](this, d);
          if (!self.selectedAction && !preventCircular) {
            self.addSelectable(this, d, element);
            self.addDrag(this, d, element);
          }
          if (preventCircular) {
            self.resizeHandles.drawResizeHandles(this, item, this.svg, {
              zoomStep: self.zoomStep
            });
          }
        });
    } catch (e) {
      this.redraw();
    }
  }

  select(id) {
    this.selection = id;
    if (id) {
      this.redrawSingle(id);
    } else {
      this.redraw();
    }
  }

  edit(id) {
    if (this.selectionItem.type === 'number') {
      this.selectedAction = ActionType.description;
      this.selection = id;
      this.showModal = true;
    }
  }

  save() {
    this.drawing = true;
    this.redraw({ preventAfterEffects: true });
    this.getOutput.emit({ ...this.model, svg: this.svg, annotations: _.cloneDeep(this.model.annotations) });
    this.drawing = false;
  }

  removeFromOutputObject(uid: string) {
    const index = this.model.annotations.items.findIndex((item) => item.id === uid);
    if (index !== -1) {
      this.model.annotations.items.splice(index, 1);
    }
    this.redraw();
  }
}
